import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastService, ToastType } from '@pin-up-global/common';
import { BehaviorSubject, distinctUntilChanged, Observable, ReplaySubject, shareReplay, switchMap, tap } from 'rxjs';
import { FeedbacksService } from '../feedbacks';
import { UserSessionApiService } from '@root/pages/user/session';
import { FeedbackApiService } from './feedback-api.service';
import { Feedback, FeedbackAdminResp, FeedbackStatus } from './feedback.abstraction';

@Injectable()
export class FeedbackService {
  #userSessionApiService = inject(UserSessionApiService);
  #feedbackApiService = inject(FeedbackApiService);
  #translateService = inject(TranslateService);
  #feedbacksService = inject(FeedbacksService);
  #toastService = inject(ToastService);

  #userId$ = new ReplaySubject<number>();
  #feedbackId$ = new ReplaySubject<number>();
  #update$ = new BehaviorSubject<void>(void 0);

  #userRes$ = this.#update$.pipe(
    switchMap(() => this.#userId$.pipe(distinctUntilChanged())),
    switchMap((userId) => this.#userSessionApiService.getUser({ userId })),
    shareReplay(1)
  );

  #feedbackRes$ = this.#update$.pipe(
    switchMap(() => this.#feedbackId$.pipe(distinctUntilChanged())),
    switchMap((feedbackId) => this.#feedbackApiService.getFeedback(feedbackId)),
    shareReplay(1)
  );

  readonly user$ = this.#userRes$;
  readonly feedback$ = this.#feedbackRes$;
  readonly isLoading$ = this.#feedbackApiService.isLoading$;

  setFeedbackId(feedbackId: number): void {
    this.#feedbackId$.next(feedbackId);
  }

  setUserId(userId: number): void {
    this.#userId$.next(userId);
  }

  sendMessage(feedbackId: number, feedback: FeedbackAdminResp): Observable<Feedback> {
    return this.#feedbackApiService.addFeedback(feedbackId, feedback).pipe(
      tap(() => {
        this.refresh();
        this.showToast(FeedbackStatus.Processed);
        this.#feedbacksService.refresh();
      })
    );
  }

  changeFeedbackStatus(feedbackId: number, status: FeedbackStatus): Observable<Feedback> {
    return this.#feedbackApiService.patchFeedback(feedbackId, status).pipe(
      tap(() => {
        this.showToast(status);
        this.#feedbacksService.refresh();
      })
    );
  }

  showToast(status: FeedbackStatus): void {
    this.#toastService.show({
      type: ToastType.Success,
      title: this.#translateService.instant(
        status === FeedbackStatus.InProgress
          ? 'feedbackPage.toast.changedStatusToInProgress'
          : 'feedbackPage.toast.changedStatusToProcessed'
      ),
    });
  }

  refresh(): void {
    this.#update$.next();
  }
}
